import React, { useEffect, useState, useCallback } from 'react';
import { useNotificationStore } from '../../store/notificationStore';
import { Notification } from '../../types';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface ToastItem {
  notification: Notification;
  visible: boolean;
  timeoutId?: ReturnType<typeof setTimeout>;
}

const TOAST_DURATION: Record<string, number> = {
  SUCCESS: 4000,
  WARNING: 6000,
  ERROR: 8000,
  SYSTEM: 5000,
  DEFAULT: 5000
};

const TOAST_STYLES: Record<string, { bg: string; border: string; icon: string; progress: string }> = {
  SUCCESS: {
    bg: 'bg-white dark:bg-neutral-800',
    border: 'border-green-200 dark:border-green-800/50',
    icon: 'text-green-500',
    progress: 'bg-green-500'
  },
  WARNING: {
    bg: 'bg-white dark:bg-neutral-800',
    border: 'border-amber-200 dark:border-amber-800/50',
    icon: 'text-amber-500',
    progress: 'bg-amber-500'
  },
  ERROR: {
    bg: 'bg-white dark:bg-neutral-800',
    border: 'border-red-200 dark:border-red-800/50',
    icon: 'text-red-500',
    progress: 'bg-red-500'
  },
  DEFAULT: {
    bg: 'bg-white dark:bg-neutral-800',
    border: 'border-primary-200 dark:border-primary-800/50',
    icon: 'text-primary-500',
    progress: 'bg-primary-500'
  }
};

const ToastIcon: React.FC<{ type: string; className?: string }> = ({ type, className }) => {
  switch (type) {
    case 'SUCCESS': return <CheckCircleIcon className={className} />;
    case 'WARNING': return <ExclamationTriangleIcon className={className} />;
    case 'ERROR': return <XCircleIcon className={className} />;
    default: return <InformationCircleIcon className={className} />;
  }
};

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const notifications = useNotificationStore(s => s.notifications);
  const lastNotifRef = React.useRef<string | null>(null);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.map(t =>
      t.notification.id === id ? { ...t, visible: false } : t
    ));
    // Remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.notification.id !== id));
    }, 300);
  }, []);

  // Watch for new notifications
  useEffect(() => {
    if (notifications.length === 0) return;

    const latest = notifications[notifications.length - 1];
    // Only show toasts for actionable types
    const toastTypes = ['SUCCESS', 'WARNING', 'ERROR', 'SYSTEM'];
    if (!toastTypes.includes(latest.type)) return;
    if (latest.id === lastNotifRef.current) return;

    lastNotifRef.current = latest.id;

    // Don't stack more than 4 toasts
    setToasts(prev => {
      const existing = prev.find(t => t.notification.id === latest.id);
      if (existing) return prev;

      const trimmed = prev.length >= 4 ? prev.slice(1) : prev;
      return [...trimmed, { notification: latest, visible: false }];
    });

    // Animate in
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setToasts(prev => prev.map(t =>
          t.notification.id === latest.id ? { ...t, visible: true } : t
        ));
      });
    });

    // Auto-dismiss
    const duration = TOAST_DURATION[latest.type] || TOAST_DURATION.DEFAULT;
    const timeoutId = setTimeout(() => dismissToast(latest.id), duration);

    return () => clearTimeout(timeoutId);
  }, [notifications, dismissToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-[99997] flex flex-col gap-2 max-w-[380px] w-full sm:w-auto pointer-events-none">
      {toasts.map(({ notification, visible }) => {
        const style = TOAST_STYLES[notification.type] || TOAST_STYLES.DEFAULT;
        const duration = TOAST_DURATION[notification.type] || TOAST_DURATION.DEFAULT;

        return (
          <div
            key={notification.id}
            className={`pointer-events-auto ${style.bg} border ${style.border} rounded-xl shadow-lg shadow-black/5 dark:shadow-black/20 overflow-hidden transition-all duration-300 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="flex items-start gap-3 px-4 py-3">
              <ToastIcon type={notification.type} className={`w-5 h-5 ${style.icon} flex-shrink-0 mt-0.5`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 leading-tight whitespace-pre-line">
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => dismissToast(notification.id)}
                className="p-0.5 rounded-md text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors flex-shrink-0"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            {/* Progress bar */}
            <div className="h-[2px] bg-neutral-100 dark:bg-neutral-700">
              <div
                className={`h-full ${style.progress} transition-none`}
                style={{
                  width: '100%',
                  animation: visible ? `toast-progress ${duration}ms linear forwards` : 'none'
                }}
              />
            </div>
          </div>
        );
      })}
      <style>{`
        @keyframes toast-progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default ToastContainer;
